import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button/Button";
import { BinIcon } from "@components/icons/common";
import { Modal } from "@components/Modal/Modal";
import { Notification } from "@components/Notification/Notification";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import {
  type IBillData,
  type IOverviewJointBillService,
} from "@services/overview/types";
import { useRemoveServiceProviderMutation } from "@services/serviceProviders/mutations";
import { isNullOrUndefined } from "@utils/common";

interface IEditBillDetailSettingsModalProps extends IUseModalProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  billData: IBillData;
}

export const RemoveServiceProviderModal: React.FC<
  IEditBillDetailSettingsModalProps
> = (props) => {
  const { jointBillServiceId, billData, ...modalProps } = props;
  const modal = useModal(modalProps);
  const { t } = useTranslation();

  const { mutateAsync: removeServiceProvider, isPending } =
    useRemoveServiceProviderMutation(
      jointBillServiceId,
      billData.jointBillServiceAgreementId,
    );

  const submitRemove = async () => {
    const response = await removeServiceProvider().catch(() => {
      toast.error(t("removeServiceProviderModal.toast.error"));
    });

    if (!isNullOrUndefined(response)) {
      toast.success(t("removeServiceProviderModal.toast.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: BinIcon, color: "red" }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("removeServiceProviderModal.buttons.cancel")}
          </Button>
          <Button
            onClick={submitRemove}
            className="w-full"
            isLoading={isPending}
            disabled={!billData.canServiceProviderBeRemoved}
          >
            {t("removeServiceProviderModal.buttons.submit")}
          </Button>
        </React.Fragment>
      }
      title={t("removeServiceProviderModal.title")}
      subtitle={billData.displayName}
      className="md:max-w-xl"
    >
      <Notification
        description={
          billData.canServiceProviderBeRemoved
            ? t("removeServiceProviderModal.description")
            : billData.isAgreementUnregistering
              ? t("bill.interactions.providerWillBeRemoved.tooltip")
              : t("removeServiceProviderModal.removeProviderDisabled")
        }
        color="red"
      />
    </Modal>
  );
};
